import React, { useState, useRef } from "react";
import { COLORS } from "../config/themes";
import { Modal, Button } from "../components";
import Icon from "./Icon";

// ─── CSV Templates ────────────────────────────────────────────────────────────

const STUDENT_HEADERS = [
  "first_name", "last_name", "middle_name", "date_of_birth", "gender",
  "classroom_name", "enrollment_date", "address",
  "guardian_name", "guardian_phone", "guardian_email", "guardian_relation",
  "guardian_employer", "guardian_work_phone",
  "parent2_name", "parent2_relation", "parent2_phone", "parent2_email",
  "parent2_employer", "parent2_work_phone",
  "allergies", "medications", "special_needs",
  "physician", "physician_phone",
  "insurance_provider", "insurance_policy",
  "authorized_pickup", "notes", "tuition_plan_name"
];

const TEACHER_HEADERS = [
  "name", "email", "phone", "extension", "role", "classroom_name", "is_substitute", "start_date"
];

const downloadCSV = (headers, filename) => {
  const csv = headers.map(h => `"${h}"`).join(",") + "\n";
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

// ─── Parse & Validate ─────────────────────────────────────────────────────────

const parseCSV = (text) => {
  const lines = text.trim().split("\n").filter(l => l.trim());
  if (lines.length < 2) return { headers: [], rows: [], error: "File appears to be empty or only has headers." };
  const headers = lines[0].split(",").map(h => h.replace(/"/g, "").trim().toLowerCase());
  const rows = lines.slice(1).map((line, i) => {
    // Handle quoted fields with commas
    const cells = [];
    let current = "";
    let inQuotes = false;
    for (const char of line) {
      if (char === '"') { inQuotes = !inQuotes; }
      else if (char === "," && !inQuotes) { cells.push(current.trim()); current = ""; }
      else { current += char; }
    }
    cells.push(current.trim());
    return { rowNum: i + 2, cells };
  });
  return { headers, rows, error: null };
};

const validateAndMapStudents = (headers, rows, classrooms) => {
  const required = ["first_name", "last_name", "guardian_name", "guardian_phone", "guardian_email"];
  const missing = required.filter(r => !headers.includes(r));
  if (missing.length > 0) return { valid: [], errors: [`Missing required columns: ${missing.join(", ")}`] };

  const idx = (name) => headers.indexOf(name);
  const valid = [];
  const errors = [];

  rows.forEach(({ rowNum, cells }) => {
    const get = (name) => (cells[idx(name)] || "").replace(/"/g, "").trim();
    const firstName = get("first_name");
    const lastName = get("last_name");
    const guardianName = get("guardian_name");
    const guardianPhone = get("guardian_phone");
    const guardianEmail = get("guardian_email");

    if (!firstName || !lastName) { errors.push(`Row ${rowNum}: Missing first or last name`); return; }
    if (!guardianName) { errors.push(`Row ${rowNum} (${firstName} ${lastName}): Missing guardian name`); return; }
    if (!guardianPhone) { errors.push(`Row ${rowNum} (${firstName} ${lastName}): Missing guardian phone`); return; }
    if (!guardianEmail) { errors.push(`Row ${rowNum} (${firstName} ${lastName}): Missing guardian email`); return; }

    // Match classroom by name (case-insensitive)
    const classroomName = get("classroom_name");
    const classroom = classrooms.find(c => c.name.toLowerCase() === classroomName.toLowerCase());

    // Parse authorized_pickup — expects "Name (Relation) Phone; Name2 (Relation2) Phone2"
    const pickupStr = get("authorized_pickup");
    const authorizedPickup = pickupStr
      ? pickupStr.split(";").map(p => {
          const m = p.trim().match(/^(.+?)\s*\((.+?)\)\s*(.*)$/);
          return m ? { name: m[1].trim(), relation: m[2].trim(), phone: m[3].trim() } : { name: p.trim(), relation: "", phone: "" };
        }).filter(p => p.name)
      : [];

    valid.push({
      id: Date.now() + Math.random(),
      firstName,
      lastName,
      middleName: get("middle_name"),
      dob: get("date_of_birth"),
      gender: get("gender"),
      classroomId: classroom?.id || null,
      address: get("address"),
      allergies: get("allergies"),
      medications: get("medications"),
      specialNeeds: get("special_needs"),
      notes: get("notes"),
      guardianName,
      guardianPhone,
      guardianEmail,
      guardianRelation: get("guardian_relation") || "Parent",
      guardianEmployer: get("guardian_employer"),
      guardianWorkPhone: get("guardian_work_phone"),
      parent2Name: get("parent2_name"),
      parent2Relation: get("parent2_relation"),
      parent2Phone: get("parent2_phone"),
      parent2Email: get("parent2_email"),
      parent2Employer: get("parent2_employer"),
      parent2WorkPhone: get("parent2_work_phone"),
      physician: get("physician"),
      physicianPhone: get("physician_phone"),
      insuranceProvider: get("insurance_provider"),
      insurancePolicy: get("insurance_policy"),
      authorizedPickup,
      status: "active",
      enrollmentDate: get("enrollment_date") || new Date().toISOString().split("T")[0],
      photo: null,
      tuitionPlanId: null,
    });
  });

  return { valid, errors };
};

const validateAndMapTeachers = (headers, rows, classrooms) => {
  const required = ["name", "email", "phone"];
  const missing = required.filter(r => !headers.includes(r));
  if (missing.length > 0) return { valid: [], errors: [`Missing required columns: ${missing.join(", ")}`] };

  const idx = (name) => headers.indexOf(name);
  const valid = [];
  const errors = [];

  rows.forEach(({ rowNum, cells }) => {
    const get = (name) => (cells[idx(name)] || "").replace(/"/g, "").trim();
    const name = get("name");
    const email = get("email");
    const phone = get("phone");

    if (!name) { errors.push(`Row ${rowNum}: Missing name`); return; }
    if (!email) { errors.push(`Row ${rowNum} (${name}): Missing email`); return; }
    if (!phone) { errors.push(`Row ${rowNum} (${name}): Missing phone`); return; }

    const classroomName = get("classroom_name");
    const classroom = classrooms.find(c => c.name.toLowerCase() === classroomName.toLowerCase());
    const isSub = ["yes", "true", "1"].includes(get("is_substitute").toLowerCase());

    valid.push({
      id: Date.now() + Math.random(),
      name,
      email,
      phone,
      extension: get("extension"),
      role: get("role") || "Lead Teacher",
      assignedRoom: classroom?.id || null,
      isSub,
      startDate: get("start_date"),
      status: "active",
      avatar: name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2),
    });
  });

  return { valid, errors };
};

// ─── Main Component ───────────────────────────────────────────────────────────

const CSVUploadModal = ({ open, onClose, type, classrooms, onImport }) => {
  const [stage, setStage] = useState("upload"); // upload | preview | done
  const [dragOver, setDragOver] = useState(false);
  const [parseErrors, setParseErrors] = useState([]);
  const [validRows, setValidRows] = useState([]);
  const [fileName, setFileName] = useState("");
  const [importing, setImporting] = useState(false);
  const fileRef = useRef();

  const isStudents = type === "students";
  const headers = isStudents ? STUDENT_HEADERS : TEACHER_HEADERS;
  const templateFile = isStudents ? "shrewcare_students_template.csv" : "shrewcare_teachers_template.csv";

  const reset = () => {
    setStage("upload");
    setParseErrors([]);
    setValidRows([]);
    setFileName("");
    setImporting(false);
  };

  const handleClose = () => { reset(); onClose(); };

  const processFile = (file) => {
    if (!file || !file.name.endsWith(".csv")) {
      setParseErrors(["Please upload a .csv file."]);
      return;
    }
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (e) => {
      const { headers: h, rows, error } = parseCSV(e.target.result);
      if (error) { setParseErrors([error]); return; }
      const result = isStudents
        ? validateAndMapStudents(h, rows, classrooms)
        : validateAndMapTeachers(h, rows, classrooms);
      setValidRows(result.valid);
      setParseErrors(result.errors);
      setStage("preview");
    };
    reader.readAsText(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    processFile(e.dataTransfer.files[0]);
  };

  const handleConfirm = async () => {
    setImporting(true);
    await onImport(validRows);
    setStage("done");
    setImporting(false);
  };

  return (
    <Modal open={open} onClose={handleClose} title={`Bulk Import ${isStudents ? "Students" : "Teachers"} via CSV`} wide>
      <div style={{ fontFamily: "inherit" }}>

        {/* ── STEP INDICATOR ── */}
        <div style={{ display: "flex", gap: 0, marginBottom: 24 }}>
          {["Upload File", "Review", "Done"].map((s, i) => {
            const stageIndex = { upload: 0, preview: 1, done: 2 }[stage];
            const active = i === stageIndex;
            const done = i < stageIndex;
            return (
              <div key={s} style={{ display: "flex", alignItems: "center", flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ width: 24, height: 24, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 800, background: done ? COLORS.success : active ? COLORS.primary : COLORS.borderLight, color: done || active ? "white" : COLORS.textMuted, flexShrink: 0 }}>
                    {done ? "✓" : i + 1}
                  </div>
                  <span style={{ fontSize: 13, fontWeight: active ? 700 : 500, color: active ? COLORS.primary : done ? COLORS.success : COLORS.textMuted }}>{s}</span>
                </div>
                {i < 2 && <div style={{ flex: 1, height: 2, background: done ? COLORS.success : COLORS.borderLight, margin: "0 12px" }} />}
              </div>
            );
          })}
        </div>

        {/* ── STAGE: UPLOAD ── */}
        {stage === "upload" && (
          <div>
            {/* Template download */}
            <div style={{ background: COLORS.primaryPale, borderRadius: 12, padding: "14px 18px", marginBottom: 20, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 14, color: COLORS.navy, marginBottom: 2 }}>Step 1 — Download the template</div>
                <div style={{ fontSize: 13, color: COLORS.textMuted }}>Fill it out with your {isStudents ? "students" : "teachers"} and save as CSV.</div>
              </div>
              <Button onClick={() => downloadCSV(headers, templateFile)}>
                ↓ Download Template
              </Button>
            </div>

            {/* Column guide */}
            <div style={{ background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 12, padding: "14px 18px", marginBottom: 20 }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: COLORS.navy, marginBottom: 10 }}>Column guide</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px 24px" }}>
                {headers.map(h => {
                  const required = isStudents
                    ? ["first_name", "last_name", "guardian_name", "guardian_phone", "guardian_email"].includes(h)
                    : ["name", "email", "phone"].includes(h);
                  return (
                    <div key={h} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}>
                      <span style={{ width: 6, height: 6, borderRadius: "50%", background: required ? COLORS.danger : COLORS.textMuted, flexShrink: 0 }} />
                      <code style={{ background: COLORS.bg, padding: "1px 6px", borderRadius: 4, fontSize: 11 }}>{h}</code>
                      {required && <span style={{ fontSize: 10, color: COLORS.danger, fontWeight: 700 }}>required</span>}
                    </div>
                  );
                })}
              </div>
              <div style={{ marginTop: 10, fontSize: 11, color: COLORS.textMuted }}>
                • <strong>classroom_name</strong> must match exactly the name of an existing classroom in your school<br />
                {isStudents
                  ? "• date_of_birth format: YYYY-MM-DD (e.g. 2020-03-15)\n                  • enrollment_date format: YYYY-MM-DD\n                  • authorized_pickup format: Name (Relation) Phone; Name2 (Relation2) Phone2"
                  : "• is_substitute: Yes or No  •  start_date format: YYYY-MM-DD"}
              </div>
            </div>

            {/* Drop zone */}
            <div
              onDrop={handleDrop}
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onClick={() => fileRef.current?.click()}
              style={{ border: `2px dashed ${dragOver ? COLORS.primary : COLORS.border}`, borderRadius: 14, padding: "32px 24px", textAlign: "center", cursor: "pointer", background: dragOver ? COLORS.primaryPale : COLORS.bg, transition: "all 0.2s" }}
            >
              <div style={{ fontSize: 32, marginBottom: 8, opacity: 0.5 }}><Icon name="daily-log" size={32} /></div>
              <div style={{ fontWeight: 700, fontSize: 15, color: COLORS.navy, marginBottom: 4 }}>Drop your CSV here or click to browse</div>
              <div style={{ fontSize: 13, color: COLORS.textMuted }}>Only .csv files are accepted</div>
              <input ref={fileRef} type="file" accept=".csv" style={{ display: "none" }} onChange={e => processFile(e.target.files[0])} />
            </div>

            {parseErrors.length > 0 && (
              <div style={{ marginTop: 16, padding: "12px 16px", background: COLORS.dangerPale, borderRadius: 10, border: `1px solid ${COLORS.danger}22` }}>
                {parseErrors.map((e, i) => <div key={i} style={{ fontSize: 13, color: COLORS.danger, fontWeight: 500 }}>⚠ {e}</div>)}
              </div>
            )}
          </div>
        )}

        {/* ── STAGE: PREVIEW ── */}
        {stage === "preview" && (
          <div>
            <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
              <div style={{ flex: 1, background: COLORS.successPale, borderRadius: 10, padding: "12px 16px", minWidth: 140 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.success, textTransform: "uppercase", letterSpacing: "0.5px" }}>Ready to import</div>
                <div style={{ fontSize: 28, fontWeight: 900, color: COLORS.success, fontFamily: "'Nunito', sans-serif" }}>{validRows.length}</div>
              </div>
              {parseErrors.length > 0 && (
                <div style={{ flex: 1, background: COLORS.dangerPale, borderRadius: 10, padding: "12px 16px", minWidth: 140 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.danger, textTransform: "uppercase", letterSpacing: "0.5px" }}>Skipped (errors)</div>
                  <div style={{ fontSize: 28, fontWeight: 900, color: COLORS.danger, fontFamily: "'Nunito', sans-serif" }}>{parseErrors.length}</div>
                </div>
              )}
            </div>

            {parseErrors.length > 0 && (
              <div style={{ marginBottom: 16, padding: "12px 16px", background: COLORS.dangerPale, borderRadius: 10, maxHeight: 120, overflow: "auto" }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.danger, marginBottom: 6 }}>Rows with errors (will be skipped):</div>
                {parseErrors.map((e, i) => <div key={i} style={{ fontSize: 12, color: COLORS.danger }}>• {e}</div>)}
              </div>
            )}

            {validRows.length > 0 && (
              <div style={{ border: `1px solid ${COLORS.border}`, borderRadius: 12, overflow: "hidden", maxHeight: 280, overflow: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: COLORS.bg }}>
                      {isStudents
                        ? ["Name", "DOB", "Classroom", "Guardian", "Phone"].map(h => <th key={h} style={{ padding: "8px 12px", textAlign: "left", fontSize: 11, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: "0.5px", borderBottom: `1px solid ${COLORS.border}` }}>{h}</th>)
                        : ["Name", "Email", "Phone", "Role", "Classroom", "Sub?"].map(h => <th key={h} style={{ padding: "8px 12px", textAlign: "left", fontSize: 11, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: "0.5px", borderBottom: `1px solid ${COLORS.border}` }}>{h}</th>)
                      }
                    </tr>
                  </thead>
                  <tbody>
                    {validRows.map((r, i) => (
                      <tr key={i} style={{ borderBottom: `1px solid ${COLORS.borderLight}` }}>
                        {isStudents ? (
                          <>
                            <td style={{ padding: "8px 12px", fontWeight: 600, color: COLORS.navy }}>{r.firstName} {r.lastName}</td>
                            <td style={{ padding: "8px 12px", color: COLORS.textMuted }}>{r.dob || "—"}</td>
                            <td style={{ padding: "8px 12px", color: r.classroomId ? COLORS.text : COLORS.danger }}>{classrooms.find(c => c.id === r.classroomId)?.name || <span style={{ color: COLORS.warning }}>⚠ Not matched</span>}</td>
                            <td style={{ padding: "8px 12px", color: COLORS.textMuted }}>{r.guardianName}</td>
                            <td style={{ padding: "8px 12px", color: COLORS.textMuted }}>{r.guardianPhone}</td>
                          </>
                        ) : (
                          <>
                            <td style={{ padding: "8px 12px", fontWeight: 600, color: COLORS.navy }}>{r.name}</td>
                            <td style={{ padding: "8px 12px", color: COLORS.textMuted }}>{r.email}</td>
                            <td style={{ padding: "8px 12px", color: COLORS.textMuted }}>{r.phone}</td>
                            <td style={{ padding: "8px 12px", color: COLORS.textMuted }}>{r.role}</td>
                            <td style={{ padding: "8px 12px", color: r.assignedRoom ? COLORS.text : COLORS.textMuted }}>{classrooms.find(c => c.id === r.assignedRoom)?.name || "—"}</td>
                            <td style={{ padding: "8px 12px", color: COLORS.textMuted }}>{r.isSub ? "Yes" : "No"}</td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div style={{ display: "flex", gap: 10, marginTop: 20, justifyContent: "space-between" }}>
              <Button variant="secondary" onClick={reset}>← Upload Different File</Button>
              <Button onClick={handleConfirm} disabled={validRows.length === 0 || importing}>
                {importing ? "Importing..." : `Import ${validRows.length} ${isStudents ? "Student" : "Teacher"}${validRows.length !== 1 ? "s" : ""}`}
              </Button>
            </div>
          </div>
        )}

        {/* ── STAGE: DONE ── */}
        {stage === "done" && (
          <div style={{ textAlign: "center", padding: "24px 0" }}>
            <div style={{ width: 64, height: 64, borderRadius: "50%", background: COLORS.successPale, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px", fontSize: 28 }}>✓</div>
            <h3 style={{ fontFamily: "'Nunito', sans-serif", fontWeight: 800, fontSize: 20, color: COLORS.navy, marginBottom: 8 }}>Import complete!</h3>
            <p style={{ fontSize: 14, color: COLORS.textMuted, marginBottom: 24 }}>
              Successfully imported <strong>{validRows.length} {isStudents ? "student" : "teacher"}{validRows.length !== 1 ? "s" : ""}</strong> from <strong>{fileName}</strong>.
            </p>
            {parseErrors.length > 0 && (
              <p style={{ fontSize: 13, color: COLORS.warning, marginBottom: 20 }}>
                {parseErrors.length} row{parseErrors.length !== 1 ? "s" : ""} were skipped due to errors. You can add those manually.
              </p>
            )}
            <Button onClick={handleClose}>Done</Button>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default CSVUploadModal;
